import axios from 'axios'
import { clearChangePasswordDefer } from './lib/changePasswordDefer'

const HTTP_TIMEOUT_MS = 60_000
/** 上传、索引等长耗时请求：不设超时 */
const LONG_RUNNING_TIMEOUT_MS = 0

export const api = axios.create({ baseURL: '/api', timeout: HTTP_TIMEOUT_MS })

export const PAGE_SIZE = 10

export type PageResult<T> = {
  items: T[]
  total: number
  page: number
  page_size: number
}

const TOKEN_KEY = 'admin_token'
const ADMIN_DISPLAY_KEY = 'admin_display_name'

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY)
}

export function getAdminDisplayName() {
  return localStorage.getItem(ADMIN_DISPLAY_KEY)
}

export function setAdminToken(token: string | null, displayName?: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
    if (displayName) localStorage.setItem(ADMIN_DISPLAY_KEY, displayName)
  } else {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(ADMIN_DISPLAY_KEY)
    clearChangePasswordDefer()
  }
}

/** 新标签页打开管理后台；已登录进 /manage，未登录进登录页 */
export function openAdminInNewTab(path = '/manage') {
  const target = getAdminToken()
    ? path
    : `/login?redirect=${encodeURIComponent(path !== '/manage' ? path : '/manage/users')}`
  window.open(target, '_blank', 'noopener,noreferrer')
}

export function formatApiError(e: unknown, fallback = '请求失败'): string {
  if (e instanceof Error && e.message) return e.message
  if (typeof e === 'string') return e
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map((x: { msg?: string }) => x.msg || '').filter(Boolean).join('; ') || fallback
  }
  return fallback
}

/** 后台 API（自动带 JWT，60s 超时） */
export const adminApi = axios.create({ baseURL: '/api', timeout: HTTP_TIMEOUT_MS })

/** 上传、索引等长耗时后台 API（不设超时） */
export const adminLongRunningApi = axios.create({ baseURL: '/api', timeout: LONG_RUNNING_TIMEOUT_MS })

function attachAuthInterceptor(inst: ReturnType<typeof axios.create>) {
  inst.interceptors.request.use((cfg) => {
    const t = getAdminToken()
    if (t) cfg.headers.Authorization = `Bearer ${t}`
    return cfg
  })
}

function rejectApiError(err: unknown, message: string): Promise<never> {
  const e = new Error(message) as Error & { httpStatus?: number }
  const status = (err as { response?: { status?: number } })?.response?.status
  if (typeof status === 'number') e.httpStatus = status
  return Promise.reject(e)
}

async function readApiErrorDetail(err: unknown): Promise<string | undefined> {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (typeof data === 'string' && data.trim()) return data.trim()
  if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const message = (detail as { message?: unknown }).message
      if (typeof message === 'string') return message
    }
    if (Array.isArray(detail)) {
      return detail
        .map((x) => {
          if (typeof x === 'string') return x
          if (x && typeof x === 'object' && 'msg' in x) {
            const item = x as { loc?: unknown[]; msg?: string }
            const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : ''
            const msg = item.msg || ''
            return loc ? `${loc}: ${msg}` : msg
          }
          return ''
        })
        .filter(Boolean)
        .join('; ')
    }
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text()
      const j = JSON.parse(text) as { detail?: unknown }
      if (typeof j.detail === 'string') return j.detail
    } catch {
      /* ignore */
    }
  }
  return undefined
}

function attachErrorHandler(inst: typeof api, opts?: { longRunning?: boolean }) {
  inst.interceptors.response.use(
    (r) => r,
    async (err) => {
      const detail = await readApiErrorDetail(err)
      if (typeof detail === 'string') {
        if (err?.response?.status === 401) {
          setAdminToken(null)
          window.dispatchEvent(new Event('admin-session-sync'))
          const reqUrl = String(err?.config?.url || '')
          const isLoginAttempt = /\/auth\/(login|sms\/login)/.test(reqUrl)
          if (!isLoginAttempt && !window.location.pathname.startsWith('/login')) {
            const redirect = encodeURIComponent(window.location.pathname + window.location.search)
            window.location.assign(`/login?redirect=${redirect}`)
          }
        }
        return rejectApiError(err, detail)
      }
      if (err?.code === 'ECONNABORTED') {
        return Promise.reject(
          new Error(
            opts?.longRunning
              ? '请求超时：服务端处理耗时过长（上传/索引可能仍在进行），请查看后端日志或稍后刷新列表'
              : '请求超时：后端未在 60 秒内响应，请检查 Docker 容器、MySQL/ES 连接及 nginx 反代',
          ),
        )
      }
      if (!err?.response) {
        return Promise.reject(
          new Error(
            opts?.longRunning
              ? '无法连接后端（请求中断）：请确认 ai-kb-backend 容器运行中，且 frontend 能访问 backend:18080'
              : '无法连接后端：请确认 ai-kb-backend / ai-kb-frontend 容器已启动，并访问正确的 HTTP 端口',
          ),
        )
      }
      return rejectApiError(err, formatApiError(err, '请求失败'))
    },
  )
}

attachAuthInterceptor(api)
attachAuthInterceptor(adminApi)
attachAuthInterceptor(adminLongRunningApi)
attachErrorHandler(api)
attachErrorHandler(adminApi)
attachErrorHandler(adminLongRunningApi, { longRunning: true })
